import { Box, Button } from 'web-library';
import { TFunction } from 'react-i18next';
import React from 'react';
import { Theme as AugmentedTheme } from '@mui/material/styles';
import { IDialogStateHandler } from '../../../modules/examRegistration/components/listing/DeleteDraftDialog';
import {
  ApplicationStatus,
  CandidateRegisteredExamListingResponse,
  ExamRegistrationType,
} from '../../../api/candidate/exam/model';
import RouteKey from '../../../routes/key';
import DeleteButton from '../../../components/buttons/DeleteButton';
import { usePostTransactionLog } from '../../../api-refactored/candidate/usePostTransactionLog';

const { exam } = RouteKey.child;
const {
  coordinatorExamRegistrations,
  coordinatorViewSummary,
  bulkUploadCandidates,
} = RouteKey.exam.child;

interface IMUIDataIndex {
  dataIndex: number;
  rowIndex: number;
}

interface IStyling {
  classes: any;
  theme: AugmentedTheme;
}

interface IHooks {
  history: any;
  t: TFunction<'translation'>;
}

interface IExamListingActionButtonsProps {
  data: CandidateRegisteredExamListingResponse[];
  styling: IStyling;
  hooks: IHooks;
  MUIDataIndex: IMUIDataIndex;
  dialogStateHandler: IDialogStateHandler;
}

interface IExamRoute {
  route: string;
  label: string;
  disableButton: boolean;
}

interface IGetExamTypeRoutesAndLabelArgs {
  data: CandidateRegisteredExamListingResponse[];
  dataIndex: number;
  t: TFunction<'translation'>;
}

const isExamOrSupplementaryRegistration = (examRegistrationType: ExamRegistrationType) =>
  examRegistrationType === ExamRegistrationType.EXAM ||
  examRegistrationType === ExamRegistrationType.SUPPLEMENTARY ||
  examRegistrationType === ExamRegistrationType.LATE_SUPPLEMENTARY;

const getExamTypeRoutesAndLabel = (args: IGetExamTypeRoutesAndLabelArgs): IExamRoute => {
  const { data, dataIndex, t } = args;
  const { examRegistrationType, applicationStatus } = data[dataIndex];
  let route: string;
  let label: string;
  let disableButton = !data[dataIndex].timeWindowOpen;
  switch (true) {
    case isExamOrSupplementaryRegistration(examRegistrationType) &&
      (applicationStatus === ApplicationStatus.REGISTERED.toUpperCase() ||
        applicationStatus === ApplicationStatus.SUBMITTED):
      route = `/${exam}/${coordinatorExamRegistrations.route}/${data[dataIndex].applicationUuid}/${coordinatorViewSummary.route}`;
      label = t('exam->>ViewRegisteredExamsListing.ViewRegistration');
      disableButton = false;
      break;
    case isExamOrSupplementaryRegistration(examRegistrationType) &&
      (applicationStatus === ApplicationStatus.DRAFT.toUpperCase() ||
        applicationStatus === ApplicationStatus.ERROR.toUpperCase() ||
        applicationStatus === ApplicationStatus.PROCESSING.toUpperCase() ||
        applicationStatus === ApplicationStatus.SUCCESS.toUpperCase()):
      route = `/${exam}/${coordinatorExamRegistrations.route}/${data[dataIndex].applicationUuid}/${bulkUploadCandidates.route}`;
      label = t('exam->>ViewRegisteredExamsListing.ContinueRegistration')
      disableButton = data[dataIndex].timeWindowOpen ? false : !data[dataIndex].supplementaryOpen;
      break;

    default:
      route = '';
      label = t('exam->>ViewRegisteredExamsListing.EmptyInstruction');
  }
  return {
    route,
    label,
    disableButton,
  };
};

const CoordinatorExamListingActionButtons = (
  ExamListingActionProps: IExamListingActionButtonsProps
) => {
  const { data, styling, hooks, MUIDataIndex, dialogStateHandler } = ExamListingActionProps;
  const {
    setDeleteDraftDialogOpen,
    setProceedSupplementaryModal,
    setDeleteExamType,
    setDeleteUuid,
  } = dialogStateHandler;
  const { dataIndex, rowIndex } = MUIDataIndex;
  const { history, t } = hooks;
  const { classes } = styling;
  const openDeleteDialogHandler = () => {
    setDeleteUuid(data[dataIndex].applicationUuid);
    setDeleteExamType(data[dataIndex].examType);
    setDeleteDraftDialogOpen(true);
  };
  const routeAndLabel = getExamTypeRoutesAndLabel({
    data,
    dataIndex,
    t,
  });

  const { mutate: apiMutation } = usePostTransactionLog({
    onSuccess: () => {
      history.push(routeAndLabel.route);
    },
  });

  const onActionButtonClicked = () => {
    if (
      data[dataIndex].applicationStatus === ApplicationStatus.REGISTERED.toUpperCase() ||
      data[dataIndex].applicationStatus === ApplicationStatus.SUBMITTED
    ) {
      history.push(routeAndLabel.route);
    } else if (
      data[dataIndex].examRegistrationType === ExamRegistrationType.EXAM &&
      data[dataIndex].supplementaryOpen
    ) {
      setDeleteUuid(data[dataIndex].applicationUuid);
      setDeleteExamType(data[dataIndex].examType);
      setProceedSupplementaryModal(true);
    } else {
      apiMutation({
        auditActionType: 'CONTINUE_EXAM_REGISTRATION',
        applicationId: `${data[dataIndex].applicationID}`,
      });
    }
  };
  return (
    <Box className={classes.actionButtons}>
      <Button
        className={classes.amendOrViewButton}
        id={`button-examRegisteredListing-row${rowIndex}`}
        variant="text"
        color="primary"
        size="small"
        onClick={onActionButtonClicked}
        disabled={routeAndLabel.disableButton}
      >
        {routeAndLabel.label}
      </Button>
      {(data[dataIndex].applicationStatus === ApplicationStatus.DRAFT.toUpperCase() ||
        data[dataIndex].applicationStatus === ApplicationStatus.ERROR.toUpperCase() ||
        data[dataIndex].applicationStatus === ApplicationStatus.PROCESSING.toUpperCase() ||
        data[dataIndex].applicationStatus === ApplicationStatus.SUCCESS.toUpperCase()) && (
        <>
          <DeleteButton
            deleteHandler={openDeleteDialogHandler}
            label={t('exam->>ViewRegisteredExamsListing.Delete')}
            buttonId={dataIndex}
          />
        </>
      )}
    </Box>
  );
};

export default CoordinatorExamListingActionButtons;
